import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiX, FiShieldCheck } = FiIcons;

const WhoThisIsFor = () => {
  const forList = [
    'Care about how your business is perceived',
    'Want to stand out from low-quality competitors',
    'Believe professionalism wins better clients',
    'Want customers to trust you before they call',
    'Have a growth mindset and think long-term'
  ];

  const notForList = [
    'Only want the cheapest possible website',
    'Are happy to blend into a sea of low-quality sites',
    'Do not care how your business appears online',
    'Expect results without putting in effort',
    'Want endless changes without a clear brief'
  ];

  return (
    <section id="who-this-is-for" className="py-20 bg-gradient-to-br from-black to-gray-900 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-4 py-2 rounded-full mb-6">
            <SafeIcon icon={FiShieldCheck} className="text-blue-400" />
            <span className="text-blue-200 text-xs font-bold uppercase tracking-widest">Client Standards</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Who This Is Built For
          </h2>
          <p className="text-xl text-white/60 max-w-3xl mx-auto">
            We build pages that represent real businesses in the real world. That means we hold a high standard, because your reputation depends on it.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Fit Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 lg:p-10"
          >
            <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <SafeIcon icon={FiCheck} className="text-green-400 w-6 h-6" />
              </div>
              A Great Fit If You:
            </h3>
            <ul className="space-y-5 mb-8">
              {forList.map((item, index) => (
                <li key={index} className="flex items-start gap-4">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500/60 mt-1 flex-shrink-0" />
                  <span className="text-white font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-green-400 font-bold text-sm bg-green-500/10 rounded-xl p-4 text-center border border-green-500/20">
              “These businesses win more than jobs. They win trust.”
            </p>
          </motion.div>

          {/* Not Right Column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 lg:p-10 opacity-70"
          >
            <h3 className="text-2xl font-bold text-white/80 mb-8 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <SafeIcon icon={FiX} className="text-red-400 w-6 h-6" />
              </div>
              May Not Be Right If You:
            </h3>
            <ul className="space-y-5">
              {notForList.map((item, index) => (
                <li key={index} className="flex items-start gap-4">
                  <SafeIcon icon={FiX} className="w-5 h-5 text-red-500/40 mt-1 flex-shrink-0" />
                  <span className="text-white/60">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto space-y-6"
        >
          <p className="text-white/80 text-lg leading-relaxed">
            We design every page for one purpose: <br />
            <span className="text-white font-bold text-xl">To make your customers feel confident choosing you.</span>
          </p>
          <p className="text-white/60">
            That only works when both sides value quality, clarity, and results. If that sounds like how you run your business, we’ll fit right in.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhoThisIsFor;